import React from 'react';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import PropTypes from 'prop-types';
import composeBuildTheme from '../../../../shared/theme/composeBuildTheme';

/* The thumbnails always draw their own mode, whatever mode the modal is in. */
const LIGHT = composeBuildTheme({ isDark: false }).colors;
const DARK = composeBuildTheme({ isDark: true }).colors;

const OPTIONS = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
];

const Options = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-top: 8px;
`;

const Option = styled('label')`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

/* The native input restyled, so it keeps arrow-key navigation and
   screen-reader semantics while matching the selected card's ring. */
const Radio = styled('input')`
  appearance: none;
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  margin: 0;
  border-radius: 50%;
  border: 1.5px solid ${({ theme }) => theme.colors.textMuted};
  background: transparent;
  cursor: pointer;

  &:checked {
    border-color: ${({ theme }) => theme.colors.text};
    background: radial-gradient(circle, ${({ theme }) => theme.colors.text} 3px, transparent 3.5px);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.text};
    outline-offset: 2px;
  }
`;

const LabelRow = styled('span')`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Frame = styled('div')`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 10;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  box-shadow: ${({ isSelected, theme }) => (isSelected ? `0 0 0 2px ${theme.colors.text}` : 'none')};
  transition: box-shadow 0.15s ease;

  ${Option}:hover & {
    box-shadow: ${({ isSelected, theme }) => (isSelected
    ? `0 0 0 2px ${theme.colors.text}`
    : `0 0 0 2px ${theme.colors.borderMedium}`)};
  }

`;

const OptionLabel = styled('span')`
  font-family: 'Nunito Sans';
  font-size: 13px;
  font-weight: ${({ isSelected }) => (isSelected ? 700 : 400)};
  color: ${({ theme, isSelected }) => (isSelected ? theme.colors.text : theme.colors.textMuted)};
`;

/* A miniature Build week: header bar, left rail, and day columns with cards. */
const Thumb = styled('div')`
  position: absolute;
  inset: 0;
  display: grid;
  grid-template-columns: 18% 1fr;
  grid-template-rows: 16% 1fr;
  background: ${({ palette }) => palette.surface};
  ${({ clipRight }) => clipRight && 'clip-path: polygon(50% 0, 100% 0, 100% 100%, 50% 100%);'}
`;

const ThumbHeader = styled('div')`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 6px;
  border-bottom: 1px solid ${({ palette }) => palette.borderLight};
`;

const Pill = styled('div')`
  height: 4px;
  width: ${({ width }) => width}px;
  border-radius: 2px;
  background: ${({ color }) => color};
`;

const ThumbRail = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px 4px;
  border-right: 1px solid ${({ palette }) => palette.borderLight};
`;

const ThumbDays = styled('div')`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 3px;
  padding: 5px;
`;

const ThumbDay = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const ThumbCard = styled('div')`
  height: ${({ height }) => height}px;
  border-radius: 2px;
  background: ${({ palette }) => palette.surfaceSecondary};
  border-left: 2px solid ${({ accent }) => accent || 'transparent'};
`;

const CARDS = [[14, 10], [10], [14, 8, 10], [8]];

const BuildThumbnail = ({ palette, accent, clipRight }) => (
  <Thumb palette={palette} clipRight={clipRight} aria-hidden='true'>
    <ThumbHeader palette={palette}>
      <Pill width={18} color={palette.text} />
      <Pill width={10} color={palette.textSecondary} />
    </ThumbHeader>
    <ThumbRail palette={palette}>
      <Pill width={10} color={accent} />
      <Pill width={10} color={palette.textSecondary} />
      <Pill width={10} color={palette.textSecondary} />
    </ThumbRail>
    <ThumbDays>
      {CARDS.map((day, dayIndex) => (
        // eslint-disable-next-line react/no-array-index-key
        <ThumbDay key={dayIndex}>
          {day.map((height, cardIndex) => (
            <ThumbCard
              // eslint-disable-next-line react/no-array-index-key
              key={cardIndex}
              height={height}
              palette={palette}
              accent={dayIndex === 0 && cardIndex === 0 ? accent : null}
            />
          ))}
        </ThumbDay>
      ))}
    </ThumbDays>
  </Thumb>
);

BuildThumbnail.propTypes = {
  palette: PropTypes.objectOf(PropTypes.string).isRequired,
  accent: PropTypes.string.isRequired,
  clipRight: PropTypes.bool,
};

BuildThumbnail.defaultProps = {
  clipRight: false,
};

/**
 * Light / Dark / System as picture cards, so the dark option is visible
 * without opening anything. Changes apply immediately, like the old select.
 */
const AppearancePicker = ({ value, onChange }) => {
  // The org's accent, so the thumbnails look like this account's Build.
  const accent = useTheme().colors.iconActive;

  const handleChange = (event) => {
    onChange(event.target.value);
  };

  return (
    <Options role='radiogroup' aria-label='Appearance'>
      {OPTIONS.map((option) => {
        const isSelected = option.value === value;
        return (
          <Option key={option.value}>
            <Frame isSelected={isSelected}>
              {option.value !== 'dark' && <BuildThumbnail palette={LIGHT} accent={accent} />}
              {option.value !== 'light' && (
              <BuildThumbnail palette={DARK} accent={accent} clipRight={option.value === 'system'} />
              )}
            </Frame>
            <LabelRow>
              <Radio
                type='radio'
                name='build-appearance'
                value={option.value}
                checked={isSelected}
                onChange={handleChange}
              />
              <OptionLabel isSelected={isSelected}>{option.label}</OptionLabel>
            </LabelRow>
          </Option>
        );
      })}
    </Options>
  );
};

AppearancePicker.propTypes = {
  value: PropTypes.oneOf(['light', 'dark', 'system']).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default AppearancePicker;
